import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, X, BookOpen } from "@phosphor-icons/react";

const VIEWED_KEY = "tt_gs_viewed_public_page";
const DISMISS_KEY = "tt_gs_dismissed";

/**
 * Getting-started checklist for new owners. Tracks real progress
 * (profile complete, first schedule posted, public page viewed) and
 * disappears once everything's done — or when dismissed.
 */
export const GettingStartedChecklist = ({ completeness, postsCount }) => {
  const [viewedPage, setViewedPage] = useState(() => localStorage.getItem(VIEWED_KEY) === "1");
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  const truckSlug = completeness?.truck_slug;
  const items = [
    {
      key: "profile",
      label: "Complete your profile",
      hint: "Logo, cuisine, menu & service area",
      done: !!completeness?.is_complete,
      to: "/dashboard/profile-editor",
    },
    {
      key: "schedule",
      label: "Post your first schedule",
      hint: "Your stops go live on the map instantly",
      done: postsCount > 0,
      to: "/dashboard/schedule-post",
    },
    {
      key: "share",
      label: "Check out your public page",
      hint: "This is what customers (and Google) see",
      done: viewedPage,
      to: truckSlug ? `/t/${truckSlug}` : "/dashboard/profile-editor",
      onClick: () => {
        localStorage.setItem(VIEWED_KEY, "1");
        setViewedPage(true);
      },
    },
  ];
  const doneCount = items.filter((i) => i.done).length;

  if (dismissed || doneCount === items.length) return null;

  return (
    <div
      className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-5"
      data-testid="getting-started-checklist"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-lg font-semibold font-display">Getting started</h2>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="getting-started-progress">
            {doneCount} of {items.length} done — finish these to start showing up for customers.
          </p>
        </div>
        <button
          onClick={() => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); }}
          className="text-muted-foreground hover:text-foreground p-1"
          aria-label="Dismiss getting started checklist"
          data-testid="getting-started-dismiss"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
      <ul className="mt-4 space-y-2">
        {items.map((i) => (
          <li key={i.key}>
            <Link
              to={i.to}
              onClick={i.onClick}
              className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${
                i.done
                  ? "border-green-200 bg-green-50/60 dark:border-green-900 dark:bg-green-950/30"
                  : "border-border bg-card hover:border-primary/50"
              }`}
              data-testid={`getting-started-item-${i.key}`}
            >
              {i.done ? (
                <CheckCircle size={22} weight="fill" className="text-green-600 flex-none" />
              ) : (
                <Circle size={22} className="text-muted-foreground flex-none" />
              )}
              <div className="min-w-0">
                <div className={`text-sm font-semibold ${i.done ? "line-through text-muted-foreground" : ""}`}>{i.label}</div>
                <div className="text-xs text-muted-foreground">{i.hint}</div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      <Link
        to="/owner-guide"
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        data-testid="getting-started-guide-link"
      >
        <BookOpen size={14} weight="bold" /> New here? Read the full Owner Guide
      </Link>
    </div>
  );
};

export default GettingStartedChecklist;
